import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import {
    ButtonActions,
    ContainerActions,
    ContainerCustomButton,
    ContainerSorts,
    ContainerTable,
    ContentHeader,
    Status,
    InputSearch,
    ContainerModifyHeader
} from "./styles";
import * as XLSX from "xlsx";
import Button from "../Buttons/Button";
import { Link } from "react-router-dom";
import chevronLeft from "../../assets/images/icons/chevron-left.svg";
import Skeleton from "../Skeleton/Skeleton";

const TableColumn = ({
                         title,
                         columns,
                         data,
                         actions,
                         sortableColumns,
                         hasExport,
                         hasSearch,
                         customButton,
                         totalPages,
                         currentPage: initialPage = 1,
                         onPageChange,
                         records = 10,
                         hasHeader = true,
                         hasModifyHeader = false,
                         inputSearchProps,
                         fetchAllData,
                         sortBy,
                         sortDirection,
                         onSort,
                         backUrl,
                         enableSelection,
                         onSelectionChange,
                         loading, // ⬅️ loading prop
                     }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const [currentPage, setCurrentPage] = useState(initialPage);
    // Follow the parent's page when it resets it (e.g. back to 1 on a new search).
    useEffect(() => {
        if (initialPage >= 1) setCurrentPage(initialPage);
    }, [initialPage]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
      if (data?.length > 0) setHasLoaded(true);
    }, [data]);

    // Stop showing skeleton after 3 seconds even if data is empty
    useEffect(() => {
      const timer = setTimeout(() => setHasLoaded(true), 3000);
      return () => clearTimeout(timer);
    }, []);

    const showSkeleton = loading || (!hasLoaded && (!data || data.length === 0));

    useEffect(() => {
        setSearchTerm(inputSearchProps?.value || "");
    }, [inputSearchProps?.value]);

    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedItems);
        }
    }, [selectedItems, onSelectionChange]);

    // Detect when a search occurs
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (value.trim() !== "") {
            setHasSearched(true);
        } else {
            setHasSearched(false);
        }

        // If parent provided its own onChange handler
        if (inputSearchProps?.onChange) {
            inputSearchProps.onChange(e);
        }
    };

    const exportData = async () => {
        setIsExporting(true);
        try {
            const allData = await fetchAllData();
            if (allData.length === 0) {
                console.error("No data available for export.");
                return;
            }
            const worksheet = XLSX.utils.json_to_sheet(allData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, title);
            XLSX.writeFile(workbook, `${title}_data.xlsx`);
        } catch (error) {
            console.error(`Error exporting data: ${error}`);
        } finally {
            setIsExporting(false);
        }
    };

    const handlePageClick = ({ selected }) => {
        const newPage = selected + 1;
        if (currentPage !== newPage) {
            setCurrentPage(newPage);
            onPageChange?.(newPage);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = data.map((row) => row.id);
            setSelectedItems(allIds);
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (id) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "long" });
        const year = date.getFullYear();
        return `${month} ${day}, ${year}`;
    };

    return (
        <ContainerTable>
            {/* Header */}
            {!hasModifyHeader && hasHeader && (
                <ContentHeader>
                    <div className="d-flex align-items-center gap-2">
                        {backUrl && (
                            <Link
                                to={backUrl}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginRight: "10px",
                                }}
                            >
                                <img src={chevronLeft} alt="Back" />
                            </Link>
                        )}
                        {title && <h2 className="page-title m-0">{title}</h2>}
                    </div>

                    <div className="d-flex flex-wrap gap-3">
                        {hasSearch && (
                            <InputSearch
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                {...inputSearchProps}
                            />
                        )}
                        {customButton && (
                            <ContainerCustomButton>{customButton}</ContainerCustomButton>
                        )}
                        {hasExport && (
                            <Button
                                className="btn-outline-light"
                                disabled={isExporting}
                                onClick={exportData}
                            >
                                {isExporting ? "Exporting..." : "Export Data"}
                            </Button>
                        )}
                    </div>
                </ContentHeader>
            )}

            {enableSelection && (
                <div className="selection-info" style={{ marginBottom: "10px" }}>
                    <strong>Total selected:</strong> {selectedItems.length}
                </div>
            )}

            {/* Table */}
            <div className="table-wrapper">
                <table>
                    <thead>
                    <tr>
                        {enableSelection && (
                            <th>
                                <input
                                    type="checkbox"
                                    checked={selectedItems.length === data.length && data.length > 0}
                                    onChange={handleSelectAll}
                                />
                            </th>
                        )}
                        {columns.map((column, index) => {
                            const isSortable = Array.isArray(sortableColumns) && sortableColumns.includes(column);
                            // The component's sort state (sortBy, sortDirection) reflects backend
                            // column names, not display column names, so we can't easily show an
                            // "active" indicator without the parent mapping it back. For now just
                            // show a small chevron on any sortable column so users see it's
                            // clickable. Parent's onSort handler toggles direction internally.
                            return (
                                <th
                                    key={index}
                                    onClick={isSortable && onSort ? () => onSort(column) : undefined}
                                    style={isSortable ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                                >
                                    {column}
                                    {isSortable && <span style={{ marginLeft: 6, opacity: 0.5, fontSize: '0.85em' }}>↕</span>}
                                </th>
                            );
                        })}
                        {actions && <th></th>}
                    </tr>
                    </thead>

                    <tbody>
                    {showSkeleton ? (
                        <>
                          {[1,2,3,4,5].map(i => (
                            <tr key={`skeleton-${i}`}>
                              {columns.map((_, colIdx) => (
                                <td key={colIdx} style={{ padding: '12px' }}>
                                  <Skeleton type="row" height="20px" />
                                </td>
                              ))}
                              {enableSelection && <td style={{ padding: '12px' }}><Skeleton type="row" height="20px" width="20px" /></td>}
                              {actions && <td style={{ padding: '12px' }}><Skeleton type="row" height="20px" width="30px" /></td>}
                            </tr>
                          ))}
                        </>
                    ) : data?.length === 0 && hasSearched ? (
                        <tr>
                            <td
                                colSpan={
                                    columns.length +
                                    (enableSelection ? 1 : 0) +
                                    (actions ? 1 : 0)
                                }
                                style={{ padding: "60px 20px" }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        flexDirection: "column",
                                        color: "#666",
                                        fontSize: "1rem",
                                        fontWeight: "500",
                                        height: "100%",
                                    }}
                                >
                                    No records found
                                </div>
                            </td>
                        </tr>
                    ) : (
                        data?.map((rowData) => (
                            <tr key={rowData.id}>
                                {enableSelection && (
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedItems.includes(rowData.id)}
                                            onChange={() => handleSelectItem(rowData.id)}
                                        />
                                    </td>
                                )}
                                {columns.map((column, colIndex) => (
                                    <Status key={colIndex} status={String(rowData[column])}>
                                        {column === "Date"
                                            ? formatDate(rowData[column])
                                            : rowData[column]}
                                    </Status>
                                ))}
                                {actions && (
                                    <ContainerActions>
                                        {/* An action with hidden(row) true is left out for that row. */}
                                        {actions.map((action, idx) => (
                                            action.hidden?.(rowData) ? null : (
                                                <ButtonActions
                                                    key={idx}
                                                    onClick={() => action.onClick(rowData)}
                                                >
                                                    {action.label}
                                                </ButtonActions>
                                            )
                                        ))}
                                    </ContainerActions>
                                )}
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {typeof totalPages === "number" && totalPages > 0 && (
                <ReactPaginate
                    pageCount={Math.ceil(totalPages / records)}
                    onPageChange={handlePageClick}
                    previousLabel={null}
                    nextLabel={
                        currentPage !== Math.ceil(totalPages / records) ? "Next" : null
                    }
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    containerClassName={"pagination"}
                    pageLinkClassName={"page-number"}
                    previousLinkClassName={"page-number-prev"}
                    nextLinkClassName={"page-number-next"}
                    activeClassName={"active-number"}
                    forcePage={currentPage - 1}
                />
            )}
        </ContainerTable>
    );
};

export default TableColumn;
